/**
 * One wording for the "sync from <DJ app>" toast across Rekordbox / Serato /
 * Traktor / VirtualDJ. Every count is the number of things that actually
 * CHANGED in MixGoat this run — never "playlists we looked at":
 *   - imported      new playlists created
 *   - updated       existing playlists that changed (renamed / reordered /
 *                   gained tracks) — reconciler `changed`, not `reconciled`
 *   - tracksCreated / tracksUpdated   library rows created / filled in
 *
 * @param {object} result   IPC result of <app>:importPlaylists
 * @param {{app: string, noun?: string}} opts   display name + playlist noun ('crate' for Serato)
 * @returns {string}
 */
export function formatDjImportSummary(result, { app, noun = 'playlist' }) {
    const r = result || {};
    const n = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
    const parts = [];
    if (r.imported > 0) parts.push(`${n(r.imported, `new ${noun}`)}`);
    if (r.updated > 0) parts.push(`${n(r.updated, noun)} updated`);
    const trackParts = [];
    if (r.tracksCreated > 0) trackParts.push(`${r.tracksCreated} new`);
    if (r.tracksUpdated > 0) trackParts.push(`${r.tracksUpdated} updated`);
    if (trackParts.length) {
        // "in library" — these are library rows, a different dimension from
        // the playlist counts (a track can be both a playlist addition and a
        // new library row; say so instead of reading like two tracks).
        const total = (r.tracksCreated || 0) + (r.tracksUpdated || 0);
        parts.push(`${trackParts.join(', ')} ${total === 1 ? 'track' : 'tracks'} in library`);
    }
    if (!parts.length) return `${app} sync: no new changes`;
    return `${app} sync: ${parts.join(', ')}`;
}

/**
 * Wording for the "Import stopped" toast. Playlists commit one at a time,
 * each together with its tracks (backend playlistBatchedImport.js), so a
 * stop leaves the first N playlists COMPLETE — say what actually made it in,
 * and that a re-run continues from there (imports are idempotent).
 */
export function formatDjImportStopped(result, { noun = 'playlist' } = {}) {
    const r = result || {};
    const n = (count, one, many = `${one}s`) => `${count.toLocaleString()} ${count === 1 ? one : many}`;
    const parts = [];
    if (r.imported > 0) parts.push(n(r.imported, `new ${noun}`));
    if (r.updated > 0) parts.push(`${n(r.updated, noun)} updated`);
    if (r.tracksCreated > 0) parts.push(`${n(r.tracksCreated, 'track')} added to your library`);
    if (!parts.length) return 'Import stopped — no changes made yet';
    return `Import stopped · ${parts.join(', ')} — run it again to continue`;
}
